import { get } from "lodash";
import { useState } from "react";
import {
  MdArrowBackIos,
  MdCheckCircle,
  MdDescription,
  MdInfo,
  MdVisibility,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Header from "shared/components/Header";
import { CONTACT_POINT_SYSTEM } from "utils/constants";

const DOCUMENTS = [
  { name: "Bulletin d'adhésion", file: "bulletin.pdf" },
  { name: "Mandat SEPA", file: "sepa_mandate.pdf" },
  { name: "Conditions générales", file: "cgv.pdf" },
];

const FORMULA_LABELS = { solo: "Solo", duo: "Duo", famille: "Famille" };

const Documents = ({ project, onNext, onBack }) => {
  const [signed, setSigned] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);

  const contactEmail = get(
    (get(project, "contact.telecoms", []) || []).find(({ system }) => system === CONTACT_POINT_SYSTEM.EMAIL),
    "value",
    "",
  );
  const totalPrice = get(project, "fields.totalPrice", 0);
  const selectedTeleconsultation = get(project, "fields.selectedTeleconsultation", "solo");
  const firstName = get(project, "contact.user.firstname", "");
  const lastName = get(project, "contact.user.lastname", "");

  const handleSign = () => {
    setSigned(true);
  };

  const handleActivate = () => {
    setNextLoading(true);
    onNext({
      payload: {
        fields: {
          ...project.fields,
          documentsSigned: true,
          activated: true,
        },
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  if (signed) {
    return (
      <>
        <Header key="header" title="Souscription finalisée" />
        <div className="contact-form" key="form_area">
          <div className="contact-form__content">
            <Form
              title="Souscription finalisée !"
              description={`Le projet a été activé avec succès. Tous les documents ont été signés électroniquement et archivés.`}
            >
              <div className="contact-stepper" key="SUCCESS">
                <div className="contact-stepper__header">
                  <div className="contact-stepper__header--counter">
                    <MdCheckCircle size={22} color="#52c41a" />
                  </div>
                  <div className="contact-stepper__header--content">
                    <h1 className="contact-stepper__header--content--title">
                      <span>Activations en cours</span>
                    </h1>
                  </div>
                </div>
                <div className="contact-stepper__content">
                  <Alert
                    showIcon
                    type="success"
                    message="API La Fourgale: Création du compte cashback mono/foyer"
                  />
                  <Alert
                    showIcon
                    type="success"
                    message={`API Stripe: Enregistrement du mandat SEPA pour prélèvement mensuel de ${totalPrice.toFixed(2)} €`}
                  />
                  <Alert
                    showIcon
                    type="success"
                    message={`Email de confirmation: Envoyé à ${contactEmail} avec tous les documents`}
                  />
                </div>
              </div>
            </Form>
          </div>
          <div className="contact-form__action">
            <Button type="primary" onClick={handleActivate} loading={nextLoading}>
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header key="header" title="Documents et Signature" />
      <div className="contact-form" key="form_area">
        <div className="contact-form__content">
          <Form
            title="Documents et Signature"
            description="Génération des documents contractuels et signature électronique"
          >
            <Alert
              showIcon
              type="info"
              icon={<MdInfo size={16} />}
              message="Signature électronique eIDAS"
              description="Les documents ci-dessous ont été générés automatiquement et sont prêts pour la signature électronique. La signature sera conforme au règlement eIDAS et aura la même valeur juridique qu'une signature manuscrite."
            />

            <div className="contact-stepper" key="DOCUMENTS">
              <div className="contact-stepper__header">
                <div className="contact-stepper__header--counter">
                  <span>1</span>
                </div>
                <div className="contact-stepper__header--content">
                  <h1 className="contact-stepper__header--content--title">
                    <span>Documents contractuels</span>
                  </h1>
                  <p className="contact-stepper__header--content--description">
                    {DOCUMENTS.length} documents à signer
                  </p>
                </div>
              </div>
              <div className="contact-stepper__content">
                {DOCUMENTS.map((doc) => (
                  <div key={doc.file} className="contact-stepper__content--box">
                    <div className="contact-stepper__content--box--header">
                      <h1 className="contact-stepper__content--box--header--title">
                        <MdDescription size={18} /> {doc.name}
                      </h1>
                      <Button type="gray--outlined" onClick={() => {}}>
                        <MdVisibility size={16} />
                        Prévisualiser
                      </Button>
                    </div>
                    <span className="text-sm">{doc.file}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="contact-stepper" key="RECAP">
              <div className="contact-stepper__header">
                <div className="contact-stepper__header--counter">
                  <span>2</span>
                </div>
                <div className="contact-stepper__header--content">
                  <h1 className="contact-stepper__header--content--title">
                    <span>Récapitulatif final</span>
                  </h1>
                </div>
              </div>
              <div className="contact-stepper__content">
                <div className="d-flex justify--between">
                  <span>Client:</span>
                  <span>{firstName} {lastName}</span>
                </div>
                <div className="d-flex justify--between">
                  <span>Email:</span>
                  <span>{contactEmail}</span>
                </div>
                <div className="d-flex justify--between">
                  <span>Formule téléconsultation:</span>
                  <span>{FORMULA_LABELS[selectedTeleconsultation]}</span>
                </div>
                <div className="d-flex justify--between">
                  <span>Montant mensuel:</span>
                  <strong className="text--primary">{totalPrice.toFixed(2)} €</strong>
                </div>
                <div className="d-flex justify--between">
                  <span>Nombre de documents:</span>
                  <span>{DOCUMENTS.length}</span>
                </div>
              </div>
            </div>
          </Form>
        </div>
        <div className="contact-form__action">
          <div className="d-flex justify--between" style={{ width: "100%" }}>
            <Button onClick={onBack} type="secondary--link">
              <MdArrowBackIos size={16} />
              Retour
            </Button>
            <Button type="primary" onClick={handleSign}>
              <MdCheckCircle size={16} />
              Signer électroniquement
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Documents;
